import React, { useCallback, useEffect, useState } from "react";
import parser from "cron-parser";
import { FormGroup, Callout, Button, MenuItem } from "@blueprintjs/core";
import { Select, ItemRenderer } from "@blueprintjs/select";
import { Table2, Column, Cell } from "@blueprintjs/table";
import { useField } from "formik";
import { NewScheduleState, ScheduleType, PeriodicSchedule, ScheduleFrequency } from "types";
import { getReadableCronExpression, parseCron, localTime, generateNumbers } from "utils/utils";

type Props = { update: (name: keyof NewScheduleState, value: string) => void };

export default function Runtime({ update }: Props) {
    const [{ value: cron }] = useField<string>("cron");
    const [{ value: startTime }] = useField<string>("startTime");
    const [{ value: startDate }] = useField<Date>("startDate");
    const [{ value: scheduleType }] = useField<ScheduleType>("scheduleType");
    const [{ value: periodic }] = useField<PeriodicSchedule>("periodic");

    const [showRuntimes, setShowRuntimes] = useState(false);
    const [times, setTimes] = useState<number>(scheduleType === "Once" ? 1 : 5);
    const [nextRuntimes, setNextRuntimes] = useState<Array<{ time: string }>>([]);
    const readableExpression = useCallback(() => {
        if (cron === "") {
            return <div className="run-text">"Invalid Selection. Please Fill Out the Corresponding Fields"</div>;
        }
        if (periodic.value) {
            if (periodic.name === "day" || periodic.name === "week") {
                return (
                    <div className="run-text">
                        {`"Starting on the 1st of ${monthNames[startDate.getMonth()]} at ${startTime} run every ${
                            periodic.value
                        } ${periodic.name}(s)"`}
                    </div>
                );
            }
            return (
                <div className="run-text">
                    {`"Starting on ${startDate.toDateString()} at ${startTime} run every ${periodic.value} ${
                        periodic.name
                    }(s)"`}
                </div>
            );
        }
        return <div className="run-text">{getReadableCronExpression(cron, startTime, startDate)}</div>;
    }, [cron, periodic.name, periodic.value, startDate, startTime]);

    useEffect(() => {
        if (cron !== "") {
            const now = new Date(startDate);
            if (scheduleType === ScheduleFrequency.Periodically) {
                const [hour, minute] = startTime.split(":");
                now.setHours(+hour);
                now.setMinutes(+minute);
                update(
                    "cron",
                    parseCron(startDate, startTime, ScheduleFrequency.Periodically, undefined, undefined, periodic)
                );
            }
            const options = {
                currentDate: now
            };

            const runtimes = [];

            const interval = parser.parseExpression(cron, options);
            if (times) {
                for (let i = 0; i < times; i++) {
                    const t = interval.next().toString();
                    runtimes.push({ time: localTime(new Date(t)) });
                }
            }
            setNextRuntimes(runtimes);
        } else {
            setShowRuntimes(false);
            setNextRuntimes([]);
            setTimes(5);
        }
    }, [cron, periodic, scheduleType, startDate, startTime, times, update]);

    const renderCell = (rowIndex: number) => <Cell>{nextRuntimes[rowIndex].time}</Cell>;

    return (
        <>
            <FormGroup key={`runtime`} label={"Run Time"} labelFor="text-input">
                <Callout>
                    {readableExpression()}
                    <div className="run-time">
                        <Button
                            minimal
                            intent="primary"
                            disabled={cron === ""}
                            onClick={() => setShowRuntimes(!showRuntimes)}
                        >
                            Next Run Time(s)
                        </Button>
                    </div>
                    {showRuntimes && (
                        <>
                            <FormGroup
                                inline
                                key={`nextruntimes`}
                                label="# of Next Run Time(s)"
                                labelFor="text-input"
                                helperText={times ? undefined : "# of Next Runtimes."}
                            >
                                <NumberSelect
                                    filterable={false}
                                    itemRenderer={renderNumbers}
                                    items={
                                        scheduleType === ScheduleFrequency.Once
                                            ? generateNumbers(1)
                                            : generateNumbers(20)
                                    }
                                    onItemSelect={(n) => {
                                        setTimes(n);
                                    }}
                                    noResults={<MenuItem disabled={true} text="No results." />}
                                    popoverProps={{ minimal: true, popoverClassName: "custom-select" }}
                                >
                                    <Button text={times ? times : "..."} rightIcon="caret-down" />
                                </NumberSelect>
                            </FormGroup>
                            {nextRuntimes.length > 0 && (
                                <Table2 defaultColumnWidth={505} numRows={nextRuntimes.length}>
                                    <Column name="Time" cellRenderer={renderCell} />
                                </Table2>
                            )}
                        </>
                    )}
                </Callout>
            </FormGroup>
        </>
    );
}

const NumberSelect = Select.ofType<number>();

const renderNumbers: ItemRenderer<number> = (n, { handleClick, modifiers }) => {
    if (!modifiers.matchesPredicate) {
        return null;
    }
    return <MenuItem active={modifiers.active} key={`choice_${n}`} onClick={handleClick} text={n} />;
};

const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
];
